// Ported from the legacy js/i18n.js STRINGS dict. Every user-facing string goes
// through here — either t(lang, 'key') for plain text, or one of the tr*() helpers in
// translate.ts for text with plurals/interpolated values. Never hardcode a display
// string elsewhere in the app; add it here instead.
import type { Lang } from '../data';

const FR = {
  chronoOrder: 'Ordre Chronologique',
  releaseOrderLbl: 'Ordre de Sortie',
  remainingLbl: 'Restantes',
  modeEssential: 'Essentiel',
  modeAll: 'Tout regarder',
  surpriseBtn: '🎲 Film surprise',
  statsBtn: '📊 Statistiques',
  filterAll: 'Tout',
  filterTodo: 'À voir',
  sortChrono: 'Chronologique',
  sortRelease: 'Ordre de sortie',
  layoutList: 'Liste',
  layoutGrid: 'Grille',
  filtersMenuBtn: 'Filtres & affichage',
  tonightLbl: 'Ce soir :',
  expandAll: 'tout déplier',
  collapseAll: 'tout replier',
  searchPlaceholder: 'Titre, acteur, réalisateur…',
  resetBtn: 'Réinitialiser ma progression',
  resetConfirm: 'Confirmer la réinitialisation ?',
  exportBtn: '↓ Exporter',
  importBtn: '↑ Importer',
  optionalBadge: 'optionnel',
  optionalNote: 'sans impact sur le fil principal',
  footerNote1:
    'Ordre chronologique interne. Séries Netflix et univers Fox marqués optionnel. Durées non sorties = estimations.',
  hoursWatchedSuffix: 'vues',
  completedSuffix: 'terminé',
  eveningsSuffix: 'soirées',
  markWatchedBtn: 'Marquer vu',
  episodeWatchedBtn: 'Épisode vu',
  upNextLbl: 'Prochain à voir',
  tonightFitsInline: 'ce soir ✓',
  tonightTag: 'ce soir',
  episodesAbbrev: 'éps',
  episodeWord: 'Épisode',
  episodeUnit: 'épisode',
  boxOfficeLbl: 'Box-office',
  boxOfficeWorldLbl: 'Box-office mondial',
  trailerBtn: '▶ Bande-annonce',
  imdbBtn: 'IMDb ↗',
  directorLbl: 'Réalisation',
  castLbl: 'Avec',
  postCreditLbl: '🎬 Post-crédit',
  postCreditUnknown: 'Non confirmé pour ce titre',
  triviaLbl: '📌 Anecdote',
  sagaLinkLbl: '🔗 Dans la saga',
  noInfoYet: "Pas encore d'informations détaillées pour ce contenu.",
  chartEmptyMsg: 'Regarde et coche des contenus sur plusieurs jours différents pour voir ta courbe de progression ici.',
  statsTitle: '📊 Statistiques',
  timeWatchedLbl: 'Temps visionné',
  completedLbl2: 'Complété',
  remainingLbl2: 'Restant',
  thisWeekLbl: 'Cette semaine',
  cumulativeProgressLbl: 'Progression cumulée',
  hoverChartHint: '— survole ou touche la courbe',
  progressByChapterLbl: 'Progression par chapitre',
  rateHint: 'Note les contenus après les avoir cochés pour voir tes stats ici.',
  tmdbNoPoster: "Pas d'affiche disponible sur TMDB pour ce titre",
  surpriseNoneLeft: '🎉 Tout est vu (parmi ce qui est sorti) !',
  exportedMsg: '↓ Progression exportée',
  importedMsg: '✓ Progression importée !',
  invalidFileMsg: '⚠ Fichier invalide',
  marathonDoneNoFuture: '🎬 Marathon terminé — bravo !',
  // Prefix for a watched-date pill (e.g. "vu le 27 juil.") — split out from fmtDayMonth
  // during the port, see src/utils/format.ts: the old app hardcoded this prefix in
  // French unconditionally, so it never actually switched to English.
  watchedOnPrefix: 'vu le',
} as const;

const EN: Record<keyof typeof FR, string> = {
  chronoOrder: 'Chronological Order',
  releaseOrderLbl: 'Release Order',
  remainingLbl: 'Remaining',
  modeEssential: 'Essential',
  modeAll: 'Watch Everything',
  surpriseBtn: '🎲 Surprise pick',
  statsBtn: '📊 Statistics',
  filterAll: 'All',
  filterTodo: 'To watch',
  sortChrono: 'Chronological',
  sortRelease: 'Release order',
  layoutList: 'List',
  layoutGrid: 'Grid',
  filtersMenuBtn: 'Filters & view',
  tonightLbl: 'Tonight:',
  expandAll: 'expand all',
  collapseAll: 'collapse all',
  searchPlaceholder: 'Title, actor, director…',
  resetBtn: 'Reset my progress',
  resetConfirm: 'Confirm reset?',
  exportBtn: '↓ Export',
  importBtn: '↑ Import',
  optionalBadge: 'optional',
  optionalNote: 'no impact on the main storyline',
  footerNote1:
    'Internal chronological order. Netflix series and the Fox universe are marked optional. Unreleased runtimes are estimates.',
  hoursWatchedSuffix: 'watched',
  completedSuffix: 'complete',
  eveningsSuffix: 'evenings',
  markWatchedBtn: 'Mark watched',
  episodeWatchedBtn: 'Episode watched',
  upNextLbl: 'Up next',
  tonightFitsInline: 'tonight ✓',
  tonightTag: 'tonight',
  episodesAbbrev: 'eps',
  episodeWord: 'Episode',
  episodeUnit: 'episode',
  boxOfficeLbl: 'Box Office',
  boxOfficeWorldLbl: 'Worldwide Box Office',
  trailerBtn: '▶ Trailer',
  imdbBtn: 'IMDb ↗',
  directorLbl: 'Director',
  castLbl: 'Starring',
  postCreditLbl: '🎬 Post-credit scene',
  postCreditUnknown: 'Not confirmed for this title',
  triviaLbl: '📌 Trivia',
  sagaLinkLbl: '🔗 Saga connection',
  noInfoYet: 'No detailed information yet for this title.',
  chartEmptyMsg: 'Watch and check off content across several different days to see your progress curve here.',
  statsTitle: '📊 Statistics',
  timeWatchedLbl: 'Time watched',
  completedLbl2: 'Completed',
  remainingLbl2: 'Remaining',
  thisWeekLbl: 'This week',
  cumulativeProgressLbl: 'Cumulative progress',
  hoverChartHint: '— hover or tap the chart',
  progressByChapterLbl: 'Progress by chapter',
  rateHint: 'Rate content after checking it off to see your stats here.',
  tmdbNoPoster: 'No poster available on TMDB for this title',
  surpriseNoneLeft: "🎉 Everything's watched (among what's out)!",
  exportedMsg: '↓ Progress exported',
  importedMsg: '✓ Progress imported!',
  invalidFileMsg: '⚠ Invalid file',
  marathonDoneNoFuture: '🎬 Marathon complete — well done!',
  watchedOnPrefix: 'watched on',
};

export const STRINGS: Record<Lang, Record<keyof typeof FR, string>> = { fr: FR, en: EN };

export type StringKey = keyof typeof FR;

export function t(lang: Lang, key: StringKey): string {
  return STRINGS[lang]?.[key] ?? STRINGS.fr[key] ?? key;
}
